import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader, Search } from 'lucide-react';
import SearchableSelect from '../common/SearchableSelect';
import { customerPortalService, getActiveStockCards } from '../../services/customerPortalApi';
import toast from 'react-hot-toast';
import {
  convertKgToMeter,
  resolveEffectiveMeterPerKg
} from '../../utils/kgToMeterConversion';
import { BaskiGorselComponent } from '../production/BaskiGorselLazyImage';

const extractNumericFamily = (desenNo) => {
  const raw = String(desenNo || '').trim();
  if (!raw) return '';
  const beforeVariant = raw.split('-')[0] || '';
  const digits = beforeVariant.match(/\d+/g);
  if (!digits) return '';
  const joined = digits.join('');
  const numeric = Number.parseInt(joined, 10);
  return Number.isFinite(numeric) ? String(numeric) : '';
};

const getVariantOrder = (desenNo) => {
  const raw = String(desenNo || '').trim();
  const match = raw.match(/-(\d+)\s*$/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  const n = Number.parseInt(match[1], 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

const filterFamilyPatterns = (allPatterns, initialDesenNo, stockCardId) => {
  const familyKey = extractNumericFamily(initialDesenNo);
  if (!familyKey) return [];

  return allPatterns
    .filter((p) => String(p.stockCardId) === String(stockCardId))
    .filter((p) => extractNumericFamily(p.desenNo) === familyKey)
    .sort((a, b) => {
      const ao = getVariantOrder(a.desenNo);
      const bo = getVariantOrder(b.desenNo);
      if (ao !== bo) return ao - bo;
      return String(a.desenNo || '').localeCompare(String(b.desenNo || ''), 'tr');
    });
};

const DigitalPrintingCreateOrderModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialPattern,
  allPatterns = [],
  stockCardsFromParent = null
}) => {
  const [loadingStockCards, setLoadingStockCards] = useState(true);
  const [stockCards, setStockCards] = useState([]);
  const [meterPerKg, setMeterPerKg] = useState(null);
  const [meterPerKgMin, setMeterPerKgMin] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  const [showMediantegSearch, setShowMediantegSearch] = useState(false);
  const [mediantegSearchTerm, setMediantegSearchTerm] = useState('');
  const [mediantegSearchResults, setMediantegSearchResults] = useState([]);
  const [mediantegAllOrders, setMediantegAllOrders] = useState([]);
  const [searchingMedianteg, setSearchingMedianteg] = useState(false);
  // janes seçim modu: 'full' = kumaş + varyantları da yükle, 'modelOnly' = sadece model/orderNo/resim
  const [mediantegSelectionMode, setMediantegSelectionMode] = useState('full');

  const [formData, setFormData] = useState({
    stockCardId: '',
    unitId: '',
    description: '',
    mediantegOrderNo: '',
    mediantegModelNo: '',
    mediantegOrderType: '',
    mediantegImageUrl: '',
    lines: [
      {
        color: '',
        kg: '',
        meter: '',
        patternCode: '',
        imageUrl: '',
        unit: 'kg'
      }
    ]
  });

  useEffect(() => {
    if (!isOpen) return;

    if (Array.isArray(stockCardsFromParent) && stockCardsFromParent.length > 0) {
      setStockCards(stockCardsFromParent);
      setLoadingStockCards(false);
      return;
    }

    const loadInitialData = async () => {
      try {
        setLoadingStockCards(true);
        const stockResponse = await getActiveStockCards();
        setStockCards(stockResponse.data || []);
      } catch (error) {
        console.error('Stok kartları yüklenemedi:', error);
        toast.error('Stok kartları yüklenemedi');
      } finally {
        setLoadingStockCards(false);
      }
    };

    loadInitialData();
  }, [isOpen, stockCardsFromParent]);

  useEffect(() => {
    if (!isOpen || !initialPattern || !stockCards.length) return;

    const stockCardIdFromPattern =
      initialPattern.stockCardId ||
      initialPattern.stock_card_id ||
      initialPattern.stockcardId ||
      initialPattern.stockcard_id ||
      null;

    const numericStockCardId = stockCardIdFromPattern ? Number(stockCardIdFromPattern) : '';

    const selectedStockCard = stockCards.find(
      (sc) => String(sc.id) === String(numericStockCardId)
    );

    const nextUnitId =
      selectedStockCard?.unitId ||
      selectedStockCard?.unit?.id ||
      selectedStockCard?.unitType?.id ||
      '';

    const init = async () => {
      try {
        if (numericStockCardId) {
          await loadMeterPerKgForStockCard(numericStockCardId);
        }

        // Tüm desenleri çek ve desen numarasının sayısal gövdesine göre varyantları bul
        // Örn: JJ00447-1, JJS00447-2, AB00447-3 -> hepsi 00447 ailesi
        let variantPatterns = [];
        if (numericStockCardId) {
          try {
            if (Array.isArray(allPatterns) && allPatterns.length > 0) {
              variantPatterns = filterFamilyPatterns(
                allPatterns,
                initialPattern.desenNo,
                numericStockCardId
              );
            } else {
              const response = await customerPortalService.get('/digital-printing/patterns', {
                params: { stockCardId: numericStockCardId }
              });
              const all = response.data?.data || [];
              variantPatterns = filterFamilyPatterns(
                all,
                initialPattern.desenNo,
                numericStockCardId
              );
            }
          } catch (e) {
            console.error('Varyant desenler yüklenemedi:', e);
          }
        }

        const variantsToUse = variantPatterns.length > 0 ? variantPatterns : [initialPattern];

        const nextLines = variantsToUse.map((p) => ({
          color: p.desenNo || '',
          kg: '',
          meter: '',
          patternCode: p.desenNo || '',
          imageUrl:
            (Array.isArray(p.gorselUrls) && p.gorselUrls.length > 0 && p.gorselUrls[0]) ||
            initialPattern.imageUrl ||
            '',
          unit: 'kg'
        }));

        setPatterns(variantsToUse);

        setFormData((prev) => ({
          ...prev,
          stockCardId: numericStockCardId || prev.stockCardId,
          unitId: nextUnitId ? Number(nextUnitId) : prev.unitId,
          // Eğer desende modelNo tanımlıysa otomatik doldur; yoksa alanı temizle
          mediantegModelNo: initialPattern.modelNo || '',
          mediantegOrderNo: '',
          mediantegOrderType: '',
          mediantegImageUrl: '',
          description: '',
          lines: nextLines
        }));
      } catch (error) {
        console.error('Dijital baskı modal ilk yükleme hatası:', error);
      }
    };

    init();
  }, [isOpen, initialPattern, stockCards, allPatterns]);

  useEffect(() => {
    if (showMediantegSearch && mediantegAllOrders.length > 0) {
      searchMediantegOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediantegSearchTerm, showMediantegSearch, mediantegAllOrders]);

  const meterPerKgNumber = useMemo(() => {
    const v = meterPerKg != null ? Number(meterPerKg) : null;
    return Number.isFinite(v) && v > 0 ? v : null;
  }, [meterPerKg]);

  const effectiveMeterPerKg = useMemo(
    () => resolveEffectiveMeterPerKg(meterPerKgNumber, meterPerKgMin),
    [meterPerKgNumber, meterPerKgMin]
  );

  const modelNoRef = useRef('');
  useEffect(() => {
    modelNoRef.current = formData.mediantegModelNo || '';
  }, [formData.mediantegModelNo]);

  useEffect(() => {
    if (!effectiveMeterPerKg) return;

    const roundMeterTo5 = (kgVal) => convertKgToMeter(kgVal, meterPerKgNumber, meterPerKgMin) ?? 0;

    setFormData((prev) => {
      let changed = false;
      const nextLines = prev.lines.map((line) => {
        const kgVal = line.kg === '' ? null : Number(line.kg);

        if ((!line.meter || line.meter === '') && Number.isFinite(kgVal) && kgVal > 0) {
          const meter = roundMeterTo5(kgVal);
          const next = { ...line, meter: String(meter) };
          changed = true;
          return next;
        }

        return line;
      });

      return changed ? { ...prev, lines: nextLines } : prev;
    });
  }, [effectiveMeterPerKg, meterPerKgNumber, meterPerKgMin]);

  if (!isOpen) {
    return null;
  }

  const handleStockCardChange = (event) => {
    const value = event.target.value;
    const numericValue = value ? Number(value) : '';
    const selectedStockCard = stockCards.find((sc) => String(sc.id) === String(numericValue));
    const nextUnitId =
      selectedStockCard?.unitId || selectedStockCard?.unit?.id || selectedStockCard?.unitType?.id || '';

    setFormData((prev) => ({
      ...prev,
      stockCardId: numericValue,
      unitId: nextUnitId ? Number(nextUnitId) : prev.unitId
    }));

    if (numericValue) {
      loadMeterPerKgForStockCard(numericValue);
      loadPatternsForCustomer({ stockCardId: numericValue, modelNo: modelNoRef.current });
    } else {
      setMeterPerKg(null);
      setMeterPerKgMin(null);
      setPatterns([]);
    }
  };

  const loadMeterPerKgForStockCard = async (stockCardId) => {
    try {
      const response = await customerPortalService.get('/digital-printing/meter-per-kg', {
        params: { stockCardId }
      });
      if (response.data?.success) {
        setMeterPerKg(response.data.data?.meterPerKg ?? null);
        setMeterPerKgMin(response.data.data?.meterPerKgMin ?? null);
      } else {
        setMeterPerKg(null);
        setMeterPerKgMin(null);
      }
    } catch (error) {
      console.error('M/KG oranı alınamadı:', error);
      setMeterPerKg(null);
      setMeterPerKgMin(null);
    }
  };

  const loadPatternsForCustomer = async ({ stockCardId, modelNo }) => {
    try {
      const params = {};
      if (stockCardId) params.stockCardId = stockCardId;
      if (modelNo) params.modelNo = modelNo;
      const response = await customerPortalService.get('/digital-printing/patterns', { params });
      if (response.data?.success) {
        setPatterns(response.data.data || []);
      } else {
        setPatterns([]);
      }
    } catch (error) {
      console.error('Desenler alınamadı:', error);
      setPatterns([]);
    }
  };

  const loadMediantegOrdersOnOpen = async () => {
    setSearchingMedianteg(true);
    try {
      const response = await customerPortalService.get('/orders/medianteg/active');
      if (response.data.success) {
        const orders = response.data.data || [];
        setMediantegAllOrders(orders);
        setMediantegSearchResults(orders);
      } else {
        setMediantegAllOrders([]);
        setMediantegSearchResults([]);
      }
    } catch (error) {
      console.error('Medianteg aktif siparişleri yüklenemedi:', error);
      toast.error('Medianteg siparişleri yüklenemedi');
      setMediantegAllOrders([]);
      setMediantegSearchResults([]);
    } finally {
      setSearchingMedianteg(false);
    }
  };

  const searchMediantegOrders = () => {
    const term = mediantegSearchTerm.trim().toLowerCase();
    if (!term) {
      setMediantegSearchResults(mediantegAllOrders);
      return;
    }

    const filtered = mediantegAllOrders.filter((order) => {
      const orderNo = (order.orderNo || '').toLowerCase();
      const modelNo = (order.modelNo || '').toLowerCase();
      return orderNo.includes(term) || modelNo.includes(term);
    });
    setMediantegSearchResults(filtered);
  };

  const loadMediantegOrderDetails = async (orderId) => {
    try {
      const response = await customerPortalService.get(`/orders/medianteg/order/${orderId}`);
      if (response.data.success) {
        const orderDetails = response.data.data;

        const stockCardIdNum = orderDetails.stockCard?.id
          ? Number(orderDetails.stockCard.id)
          : '';
        const unitIdNum = orderDetails.stockCard?.unitId
          ? Number(orderDetails.stockCard.unitId)
          : '';

        setFormData((prev) => ({
          ...prev,
          // full modda kumaş + birim de güncellensin, modelOnly modda dokunma
          stockCardId:
            mediantegSelectionMode === 'full' ? stockCardIdNum || prev.stockCardId : prev.stockCardId,
          unitId:
            mediantegSelectionMode === 'full' ? unitIdNum || prev.unitId : prev.unitId,
          mediantegOrderNo: orderDetails.orderNo || prev.mediantegOrderNo,
          // Model alanı boşsa doldur, doluysa koru
          mediantegModelNo:
            prev.mediantegModelNo && prev.mediantegModelNo.trim()
              ? prev.mediantegModelNo
              : orderDetails.modelNo || prev.mediantegModelNo,
          mediantegImageUrl: orderDetails.imageUrl || prev.mediantegImageUrl,
          mediantegOrderType: orderDetails.orderType || prev.mediantegOrderType || '',
          description: orderDetails.description || prev.description
        }));

        if (mediantegSelectionMode === 'full' && stockCardIdNum) {
          loadMeterPerKgForStockCard(stockCardIdNum);
          loadPatternsForCustomer({ stockCardId: stockCardIdNum, modelNo: orderDetails.modelNo });
        }

        toast.success('Janes sipariş bilgileri yüklendi');
        setShowMediantegSearch(false);
        setMediantegSearchTerm('');
        setMediantegSearchResults([]);
      } else {
        toast.error(response.data.message || 'Sipariş detayları yüklenemedi');
      }
    } catch (error) {
      console.error('Medianteg sipariş detay hatası:', error);
      const message =
        error.response?.data?.message ||
        error.message ||
        'Sipariş detayları yüklenirken hata oluştu';
      toast.error(message);
    }
  };

  const handleVariantQuantityChange = (index, value) => {
    setFormData((prev) => {
      const nextLines = [...prev.lines];
      const current = nextLines[index] || {};
      const unit = current.unit || 'kg';

      const next = { ...current };
      if (unit === 'kg') {
        next.kg = value;
        next.meter = '';
      } else {
        next.meter = value;
        next.kg = '';
      }

      nextLines[index] = next;
      return { ...prev, lines: nextLines };
    });
  };

  const handleVariantUnitChange = (index, unit) => {
    setFormData((prev) => {
      const nextLines = [...prev.lines];
      const current = nextLines[index] || {};
      const currentUnit = current.unit || 'kg';
      const currentQuantity =
        currentUnit === 'kg' ? current.kg : current.meter;

      const next = {
        ...current,
        unit,
        kg: unit === 'kg' ? currentQuantity : current.kg,
        meter: unit === 'm' ? currentQuantity : current.meter
      };

      nextLines[index] = next;
      return { ...prev, lines: nextLines };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.stockCardId) {
      toast.error('Lütfen kumaş seçin');
      return;
    }

    if (!formData.unitId) {
      toast.error('Lütfen birim seçin');
      return;
    }

    if (!formData.mediantegModelNo || !String(formData.mediantegModelNo).trim()) {
      toast.error('Lütfen Janes model numarasını giriniz veya siparişleriniz arasından seçiniz.');
      return;
    }

    if (!formData.mediantegOrderNo || !String(formData.mediantegOrderNo).trim()) {
      toast.error('Lütfen Janes siparişini listeden seçiniz.');
      return;
    }

    if (!formData.mediantegOrderType || !String(formData.mediantegOrderType).trim()) {
      toast.error('Seçilen Janes siparişinde sipariş tipi bulunamadı. Lütfen siparişi tekrar seçiniz.');
      return;
    }

    if (!Array.isArray(formData.lines) || formData.lines.length === 0) {
      toast.error('En az bir varyant ekleyin');
      return;
    }

    // En az bir varyantta miktar girilmiş olmalı
    const linesWithQuantity = formData.lines.filter(
      (line) =>
        (line.kg && Number(line.kg) > 0) || (line.meter && Number(line.meter) > 0)
    );
    if (linesWithQuantity.length === 0) {
      toast.error('En az bir varyant için kg veya metre girin');
      return;
    }

    // Miktar girilen satırlar için desen zorunlu
    const hasInvalidLine = linesWithQuantity.some((line) => {
      const hasPattern = line.patternCode && String(line.patternCode).trim();
      return !hasPattern;
    });
    if (hasInvalidLine) {
      toast.error('Miktar girilen her satır için desen seçimi zorunludur');
      return;
    }

    // Backend tarafında boş satırlar zaten yok sayılacak; yine de payload'a tamamını gönderiyoruz
    const linesPayload = formData.lines.map((line) => ({
      color: line.color || null,
      kg: line.kg ? Number(line.kg) : null,
      meter: line.meter ? Number(line.meter) : null,
      patternCode: line.patternCode || null,
      imageUrl: line.imageUrl || formData.mediantegImageUrl || null,
      unit: line.unit || null
    }));

    try {
      setFormLoading(true);
      const payload = {
        stockCardId: Number(formData.stockCardId),
        unitId: Number(formData.unitId),
        description: formData.description || null,
        mediantegOrderNo: formData.mediantegOrderNo || null,
        mediantegModelNo: formData.mediantegModelNo || null,
        mediantegOrderType: formData.mediantegOrderType || null,
        mediantegImageUrl: formData.mediantegImageUrl || null,
        lines: linesPayload
      };

      const response = await customerPortalService.createDigitalPrintingOrder(payload);
      if (response.success) {
        toast.success('Baskı siparişi oluşturuldu');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.message || 'Baskı siparişi oluşturulamadı');
      }
    } catch (error) {
      console.error('Baskı siparişi oluşturulamadı:', error);
      const message =
        error.response?.data?.message || error.message || 'Baskı siparişi oluşturulamadı';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Dijital Baskı Siparişi Oluştur
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-4 sm:px-6 sm:py-5 space-y-4 sm:space-y-6">
          {/* Model No + Kumaş seçimi */}
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Model No */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Model No <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={formData.mediantegModelNo || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        mediantegModelNo: e.target.value,
                        // Model manuel değişirse eski Janes eşleşmesini temizle
                        mediantegOrderNo: '',
                        mediantegOrderType: ''
                      }))
                    }
                    placeholder="Örn. 12036"
                    className={`flex-1 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white ${
                      formData.mediantegOrderNo
                        ? 'border border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-600'
                        : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setMediantegSelectionMode('modelOnly');
                      setMediantegSearchTerm(String(formData.mediantegModelNo || '').trim());
                      loadMediantegOrdersOnOpen();
                      setShowMediantegSearch(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium"
                  >
                    <Search className="w-3.5 h-3.5" />
                    <span>Ara</span>
                  </button>
                </div>
                {(formData.mediantegOrderNo || formData.mediantegOrderType) && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                    {formData.mediantegOrderNo && (
                      <div>
                        Sipariş No: <span className="font-semibold text-gray-700 dark:text-gray-200">{formData.mediantegOrderNo}</span>
                      </div>
                    )}
                    {formData.mediantegOrderType && (
                      <div>
                        Sipariş Tipi: <span className="font-semibold text-gray-700 dark:text-gray-200">{formData.mediantegOrderType}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Kumaş seçimi */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Kumaş (stok kartı) <span className="text-red-500">*</span>
                </label>
                {loadingStockCards ? (
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Yükleniyor...
                  </div>
                ) : (
                  <SearchableSelect
                    options={stockCards}
                    value={formData.stockCardId}
                    onChange={handleStockCardChange}
                    placeholder="Kumaş seçin"
                    searchPlaceholder="Kumaş ara..."
                    getOptionLabel={(card) => card.name}
                    getOptionValue={(card) => card.id}
                    name="stockCardId"
                  />
                )}
              </div>
            </div>
          </div>

          {/* M/KG oranı otomatik (stok kartına göre) */}
          {formData.stockCardId && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {effectiveMeterPerKg ? (
                <span>
                  Stok kartına göre min m/kg bandı (yaklaşık): {effectiveMeterPerKg} m/kg
                </span>
              ) : (
                <span>
                  Bu stok kartı için m/kg oranı bulunamadı. Kg veya metre girdikten sonra dönüşüm
                  yapılamaz.
                </span>
              )}
            </div>
          )}

          {/* Varyantlar */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Varyantlar
              </label>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {patterns.map((pattern, index) => {
                const line = formData.lines[index] || {};
                const unit = line.unit || 'kg';
                const quantityValue = unit === 'kg' ? line.kg : line.meter;

                let approxMeterText = '';
                const qtyNum = Number(unit === 'kg' ? line.kg : line.meter);
                if (unit === 'kg' && effectiveMeterPerKg && Number.isFinite(qtyNum) && qtyNum > 0) {
                  const rounded = convertKgToMeter(qtyNum, meterPerKgNumber, meterPerKgMin);
                  approxMeterText = rounded != null ? `Yaklaşık ${rounded} m` : '';
                }

                const imageUrl =
                  (Array.isArray(pattern.gorselUrls) &&
                    pattern.gorselUrls.length > 0 &&
                    pattern.gorselUrls[0]) ||
                  line.imageUrl ||
                  '';

                const hasQuantity =
                  (line.kg && Number(line.kg) > 0) || (line.meter && Number(line.meter) > 0);

                return (
                  <div
                    key={pattern.id || pattern.desenNo || index}
                    className={`rounded-md p-2 flex flex-col gap-2 transition-colors ${
                      hasQuantity
                        ? 'border-2 border-green-400 bg-green-50 dark:bg-green-900/20'
                        : 'border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {/* Desen no üstte */}
                    <div className="text-xs font-semibold text-gray-900 dark:text-white mb-1">
                      {pattern.desenNo}
                    </div>

                    {/* Büyük görsel */}
                    <div className="w-full aspect-[3/4] rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      {imageUrl ? (
                        <BaskiGorselComponent
                          imageUrl={imageUrl}
                          alt={pattern.desenNo}
                          useThumbnail
                          thumbnailSize={240}
                          priority
                        />
                      ) : (
                        <div className="text-[10px] text-gray-400 text-center px-1">
                          Görsel yok
                        </div>
                      )}
                    </div>

                    {/* Miktar + birim (küçük, altta) */}
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="number"
                        min="0"
                        step={unit === 'kg' ? '0.001' : '0.1'}
                        value={quantityValue || ''}
                        onChange={(e) => handleVariantQuantityChange(index, e.target.value)}
                        placeholder={unit === 'kg' ? 'Kg' : 'Metre'}
                        className="w-20 border border-gray-300 dark:border-gray-600 rounded-md px-1.5 py-1 text-[11px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                      <select
                        className="w-14 border border-gray-300 dark:border-gray-600 rounded-md px-1.5 py-1 text-[11px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={unit}
                        onChange={(e) => handleVariantUnitChange(index, e.target.value)}
                      >
                        <option value="kg">Kg</option>
                        <option value="m">m</option>
                      </select>
                    </div>

                    {unit === 'kg' && approxMeterText && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">
                        {approxMeterText}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Açıklama */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Açıklama (opsiyonel)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="Ek bilgi yazabilirsiniz"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={formLoading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {formLoading ? 'Gönderiliyor...' : 'Baskı Siparişi Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
    {showMediantegSearch && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              Medianteg Sipariş Ara
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowMediantegSearch(false);
                setMediantegSearchTerm('');
              }}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={mediantegSearchTerm}
                onChange={(e) => setMediantegSearchTerm(e.target.value)}
                placeholder="Sipariş no veya model no girin..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {searchingMedianteg ? (
              <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Siparişler yükleniyor...
              </div>
            ) : mediantegSearchResults.length === 0 ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                Aktif sipariş bulunamadı
              </div>
            ) : (
              <div className="space-y-2">
                {mediantegSearchResults.map((order) => (
                  <button
                    key={order.orderId}
                    type="button"
                    onClick={() => loadMediantegOrderDetails(order.orderId)}
                    className="w-full text-left border border-gray-200 dark:border-gray-700 rounded-md p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex gap-3"
                  >
                    {order.imageUrl && (
                      <div className="w-14 h-14 flex-shrink-0 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <BaskiGorselComponent
                          imageUrl={order.imageUrl}
                          alt="Sipariş"
                          useThumbnail
                          thumbnailSize={112}
                          priority={false}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {order.orderNo}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Model: {order.modelNo || '-'}
                          </p>
                        </div>
                      </div>
                      {order.fabric && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Kumaş: {order.fabric}
                        </p>
                      )}
                      {order.orderType && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Sipariş Tipi: {order.orderType}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default DigitalPrintingCreateOrderModal;

